<template>
  <div class="version-history">
    <div class="history-safety">
      <i class="fas fa-shield-alt"></i>
      <div>
        <strong>Você sempre pode voltar</strong>
        <span
          >Publicar cria uma nova versão e mantém as anteriores
          disponíveis.</span
        >
      </div>
    </div>

    <div v-if="!history.length" class="history-empty">
      Ainda não há versões para este modelo.
    </div>

    <div v-for="version in history" :key="version.id" class="history-row">
      <div class="history-line">
        <span :class="['history-dot', statusClass(version)]"><i></i></span>
      </div>
      <div class="history-content">
        <div class="d-flex align-items-start justify-content-between">
          <div>
            <div class="d-flex align-items-center flex-wrap">
              <strong>Versão {{ version.numero }}</strong>
              <span :class="['badge ml-2', badgeClass(version)]">{{
                statusLabel(version)
              }}</span>
            </div>
            <small>{{ dateLabel(version) }}</small>
          </div>
          <button
            v-if="canRestore(version)"
            type="button"
            class="btn btn-xs btn-light-primary font-weight-bold"
            @click="$emit('restore', version)"
          >
            Restaurar
          </button>
        </div>
        <p v-if="version.observacao">{{ version.observacao }}</p>
        <span v-if="version.id_versao_origem" class="origin-note">
          Criada a partir da versão {{ sourceNumber(version.id_versao_origem) }}
        </span>
      </div>
    </div>

    <div v-if="origin === 'legado' && hasPublication" class="legacy-return">
      <div>
        <strong>Voltar para a etiqueta original</strong>
        <span
          >Desativa a versão do editor e reativa imediatamente o motor legado
          protegido.</span
        >
      </div>
      <button
        type="button"
        class="btn btn-sm btn-outline-danger font-weight-bold"
        @click="$emit('reactivate-legacy')"
      >
        Reativar legado
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "EtiquetaVersionHistory",
  props: {
    history: { type: Array, default: () => [] },
    publishedId: { type: Number, default: null },
    origin: { type: String, default: "novo" },
    hasPublication: { type: Boolean, default: false },
  },
  methods: {
    isCurrent(version) {
      return Number(version.id) === Number(this.publishedId);
    },
    canRestore(version) {
      return version.status !== "rascunho" && !this.isCurrent(version);
    },
    statusLabel(version) {
      if (this.isCurrent(version)) return "Em uso";
      return (
        {
          rascunho: "Rascunho",
          publicado: "Publicada",
          substituido: "Anterior",
          arquivado: "Arquivada",
        }[version.status] || version.status
      );
    },
    badgeClass(version) {
      if (this.isCurrent(version)) return "badge-light-success";
      return version.status === "rascunho"
        ? "badge-light-warning"
        : "badge-light";
    },
    statusClass(version) {
      if (this.isCurrent(version)) return "current";
      return version.status === "rascunho" ? "draft" : "old";
    },
    dateLabel(version) {
      const value = version.publicado_em || version.atualizado_em;
      if (!value) return "Data não informada";
      return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));
    },
    sourceNumber(id) {
      const source = this.history.find(
        (item) => Number(item.id) === Number(id)
      );
      return source ? source.numero : "anterior";
    },
  },
};
</script>

<style lang="scss" scoped>
.version-history {
  color: #3f4254;
}

.history-safety {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding: 13px;
  border-radius: 9px;
  background: #e8fff3;
  color: #16875f;

  i {
    font-size: 20px;
  }

  strong,
  span {
    display: block;
  }

  span {
    font-size: 11px;
    margin-top: 2px;
  }
}

.history-row {
  display: flex;
  min-height: 86px;
}

.history-line {
  position: relative;
  width: 24px;
  flex: 0 0 24px;

  &::after {
    content: "";
    position: absolute;
    left: 7px;
    top: 18px;
    bottom: -6px;
    border-left: 1px solid #e4e6ef;
  }
}

.history-row:last-of-type .history-line::after {
  display: none;
}

.history-dot {
  position: absolute;
  left: 0;
  top: 4px;
  width: 15px;
  height: 15px;
  border: 3px solid #fff;
  border-radius: 50%;
  box-shadow: 0 0 0 1px #d8dbe5;
  background: #b5b5c3;

  &.current {
    background: #1bc5bd;
    box-shadow: 0 0 0 1px #1bc5bd;
  }

  &.draft {
    background: #ffa800;
    box-shadow: 0 0 0 1px #ffa800;
  }
}

.history-content {
  flex: 1;
  padding: 0 0 17px 6px;

  strong {
    font-size: 12px;
  }

  small {
    display: block;
    color: #a1a5b7;
    font-size: 9px;
    margin-top: 3px;
  }

  p {
    margin: 8px 0 0;
    color: #5e6278;
    font-size: 11px;
  }
}

.origin-note {
  display: inline-block;
  margin-top: 6px;
  color: #8950fc;
  font-size: 9px;
}

.history-empty {
  padding: 25px;
  text-align: center;
  color: #a1a5b7;
  font-size: 12px;
}

.legacy-return {
  margin-top: 14px;
  padding: 15px;
  border: 1px solid #f2c6cc;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;

  strong,
  span {
    display: block;
  }

  strong {
    font-size: 12px;
  }

  span {
    color: #7e8299;
    font-size: 10px;
    margin-top: 3px;
  }
}
</style>
